import {
  BillImportMatchRequest,
  BillImportMatchResponse,
  CustomerMatchResult,
  DuplicateResolution
} from '../../../models/bill-import.models';
import { ParsedBillData } from '../../../models';
import {
  BillAiFlowStep,
  BillAiFlowStepConfig,
  BillNavEntry,
  MeterEditData,
  SaEditData,
  NewCustomerContact,
  NewCustomerDetails,
  FastTrackAnalysis
} from './bill-ai-flow.models';

/** Snapshot stored on merge/delete undo stacks. Every merge/delete rewrites
 *  the match request alongside the response, so both are always captured. */
export interface UndoSnapshot {
  matchResponse: BillImportMatchResponse;
  entitySelections: Record<string, number | string | null>;
  matchRequest: BillImportMatchRequest;
}

export interface BillAiFlowState {
  // Navigation
  currentStep: BillAiFlowStep;
  completedSteps: string[];
  steps: BillAiFlowStepConfig[];
  currentBillIndex: number;

  // Loading
  isLoading: boolean;
  isMatching: boolean;
  error: string | null;

  // Match data (from API)
  matchRequest: BillImportMatchRequest | null;
  matchResponse: BillImportMatchResponse | null;
  customerMatch: CustomerMatchResult | null;

  // Customer selection
  selectedCustomerId: number | string | null;
  selectedCustomerLabel: string;
  treatAsNew: boolean;
  createdCustomerUpfront: boolean;
  newCustomerContact: NewCustomerContact;
  newCustomerDetails: NewCustomerDetails;

  // Entity selections & edits (Record instead of Map for serialization)
  entitySelections: Record<string, number | string | null>;
  /** True when a selection changed after the last match — a re-match would narrow candidate pools. */
  selectionsDirtySinceMatch: boolean;
  meterEdits: Record<string, MeterEditData>;
  saEdits: Record<string, SaEditData>;

  // Bill data — stored directly in the store (the key fix)
  billData: Record<string, ParsedBillData>;
  billFileIdMapping: Record<string, string>;  // groupId → billFile.id
  mergedBills: Record<string, string>;  // groupId → mergedInto groupId
  billNavigation: BillNavEntry[];

  // Duplicates
  duplicatesMap: Record<string, import('../../../models/bill-import.models').DuplicateWarning>;
  duplicateResolutions: Record<string, DuplicateResolution>;
  /** Pairable suggestions the user declined — the bill reverts to a plain blocking overlap. */
  rejectedPairSuggestions: Record<string, boolean>;
  /** One-shot: the express bill overview should open straight onto the current bill (not persisted). */
  openBillDetailRequested: boolean;

  // Entity name overrides (groupId → user-edited name)
  entityNameOverrides: Record<string, string>;

  // LEN merge
  lenMergeSelection: string[];
  /** Undo stack for LEN merge operations — NOT persisted to localStorage */
  lenMergeUndoStack: UndoSnapshot[];
  /** Undo stack for LEN delete operations — NOT persisted to localStorage */
  lenDeleteUndoStack: UndoSnapshot[];

  // SA merge
  saMergeSelection: string[];
  /** Undo stack for SA merge operations — NOT persisted to localStorage */
  saMergeUndoStack: UndoSnapshot[];
  /** Undo stack for SA delete operations — NOT persisted to localStorage */
  saDeleteUndoStack: UndoSnapshot[];

  // Meter merge
  meterMergeSelection: string[];
  /** Undo stack for meter merge operations — NOT persisted to localStorage */
  meterMergeUndoStack: UndoSnapshot[];
  /** Undo stack for meter delete operations — NOT persisted to localStorage */
  meterDeleteUndoStack: UndoSnapshot[];

  // Preview
  previewBillFileId: string | null;

  // Fast Track
  fastTrackMode: 'off' | 'fast' | 'review';
  fastTrackAnalysis: FastTrackAnalysis | null;
  fastTrackSkippedSteps: BillAiFlowStep[];
  fastTrackEnergyPlanChoice: 'now' | 'later' | null;

  // Final Status
  importSuccess: boolean;

  // Close request (e.g., user deleted the last entity)
  closeRequested: boolean;
}

export const initialBillAiFlowState: BillAiFlowState = {
  currentStep: 'intro',
  completedSteps: [],
  steps: [],
  currentBillIndex: -1,

  isLoading: false,
  isMatching: false,
  error: null,

  matchRequest: null,
  matchResponse: null,
  customerMatch: null,

  selectedCustomerId: null,
  selectedCustomerLabel: '',
  treatAsNew: false,
  createdCustomerUpfront: false,
  newCustomerContact: { firstName: '', lastName: '', email: '', phone: '', title: '' },
  newCustomerDetails: { customerName: '', address1: '', address2: '', city: '', state: '', zip: '' },

  entitySelections: {},
  selectionsDirtySinceMatch: false,
  meterEdits: {},
  saEdits: {},

  billData: {},
  billFileIdMapping: {},
  mergedBills: {},
  billNavigation: [],

  duplicatesMap: {},
  duplicateResolutions: {},
  rejectedPairSuggestions: {},
  openBillDetailRequested: false,

  entityNameOverrides: {},
  lenMergeSelection: [],
  lenMergeUndoStack: [],
  lenDeleteUndoStack: [],
  saMergeSelection: [],
  saMergeUndoStack: [],
  saDeleteUndoStack: [],
  meterMergeSelection: [],
  meterMergeUndoStack: [],
  meterDeleteUndoStack: [],
  previewBillFileId: null,
  fastTrackMode: 'off',
  fastTrackAnalysis: null,
  fastTrackSkippedSteps: [],
  fastTrackEnergyPlanChoice: null,
  importSuccess: false,
  closeRequested: false,
};
